import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A KPI tile.
 *
 * The delta is the interesting design decision. "Up" is not automatically good — a rising DLQ
 * depth or a rising reservation-release rate is an incident, while a rising match-success rate is
 * healthy. So the caller declares `higherIsBetter`, and the tile colours the delta by *meaning*
 * rather than by direction, with the arrow still showing the direction so nothing is hidden.
 */

/** Props for {@link StatTile}. */
export interface StatTileProps {
  /** KPI name. */
  label: string;
  /** Formatted current value. */
  value: ReactNode;
  /** Current numeric value, for the delta calculation. */
  current?: number | undefined;
  /** Value one comparison window ago. */
  previous?: number | undefined;
  /** Whether an increase is a good thing. Omit to show the delta as neutral. */
  higherIsBetter?: boolean | undefined;
  /** One line explaining what the number means. */
  hint?: string | undefined;
  /** Optional sparkline or chart. */
  chart?: ReactNode | undefined;
  /** Status chip shown top-right. */
  badge?: ReactNode | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

function formatDelta(current: number, previous: number): string {
  if (previous === 0) return current === 0 ? '0%' : 'new';
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const rounded = Math.abs(pct) < 0.05 ? 0 : pct;
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}%`;
}

/** A single dashboard statistic. */
export function StatTile({
  label,
  value,
  current,
  previous,
  higherIsBetter,
  hint,
  chart,
  badge,
  className,
}: StatTileProps): ReactNode {
  const hasDelta = current !== undefined && previous !== undefined;
  const rising = hasDelta && current > previous;
  const flat = hasDelta && Math.abs(current - previous) < Number.EPSILON;
  const good = higherIsBetter === undefined ? null : rising === higherIsBetter;

  const Icon = flat ? Minus : rising ? TrendingUp : TrendingDown;

  return (
    <div
      className={cn('flex flex-col gap-3 rounded-lg border border-border bg-card p-4', className)}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        {badge}
      </div>

      <div className="flex flex-wrap items-baseline gap-2">
        <span className="tnum text-2xl font-semibold tracking-tight">{value}</span>
        {hasDelta ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-2xs font-medium',
              flat || good === null
                ? 'bg-muted text-muted-foreground'
                : good
                  ? 'bg-state-good-bg text-state-good'
                  : 'bg-state-bad-bg text-state-bad',
            )}
          >
            <Icon className="size-3" aria-hidden="true" />
            <span className="tnum">{formatDelta(current, previous)}</span>
            <span className="sr-only">
              {' '}
              compared with four hours ago
              {good === null ? '' : good ? ', an improvement' : ', a regression'}
            </span>
          </span>
        ) : null}
      </div>

      {chart}

      {hint ? <p className="text-2xs leading-relaxed text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
